<script lang="ts">
    import {getUsers} from "../../../services/api/data-handler.js";
    import type {User, UsersData} from "../../../services/api/types.js";
    import {editObject, drawerStore} from "../../../services/stores/object-drawer-store.js";
    import {onMount} from "svelte";
    import {createGrid} from "ag-grid-community";
    import type {GridOptions, GridApi} from "ag-grid-community";
    import {navigateTo} from "../../../services/routing/route-action.js";
    import {theme} from "../../../services/stores/theme-store.js";
    import GridHeader from "../../common/GridHeader.svelte";
    import {getSVGIcon} from "../../../services/utils.js";
    import DeleteObjectDialog from "../../dialogs/DeleteObjectDialog.svelte";
    import {get} from 'svelte/store';
    import {userStore} from '../../../services/stores/user-store.js';
    import { userRole } from "../../../helpers/helpers.js";
    import { formatToProperCase, formatUserType } from "../../../helpers/helpers.js";
    import { logError } from "../../../services/logging/log-handler.js";

    let deleteDialogOpen = false;
    let objectToDelete: any = null;

    let gridOptions: GridOptions;
    let gridApi: GridApi;
    let usersData: UsersData;
    let users: User[] = [];

    let canManageUsers = false;

    interface Customer {
        name: string;
        [key: string]: any;
    }

    async function loadUsers() {
        try {
            usersData = await getUsers();
            users = usersData.users.map(user => ({
                ...user,
                customers: Array.isArray(user.customers) ? user.customers.filter(Boolean) : []
            }));
            updateGridData();
        } catch (error) {
            logError(error, "UserGrid", undefined, { message: "Error loading users" });
        }
    }

    function updateGridData() {
        if (gridApi && users) {
            setTimeout(() => {
                gridApi.setGridOption('rowData', users);
                gridApi.sizeColumnsToFit();
                gridApi.autoSizeAllColumns();
            }, 0);
        }
    }

    // Update theme mode on the grid container when theme changes
    $: {
      const gridElement = document.querySelector("#userGrid");
      if (gridElement) {
        gridElement.setAttribute("data-ag-theme-mode", $theme === "dark" ? "dark" : "light");
      }
    }

    $: if ($drawerStore.object && $drawerStore.objectType === "user") {
        loadUsers();
    }

    onMount(() => {
        
        const store = get(userStore);
        canManageUsers = store.claims?.canManageUsers ?? false;

        (async () => {
            await loadUsers();
            gridOptions = {
                defaultColDef: {
                    sortable: true,
                    filter: true,
                    resizable: true
                },
                autoSizeStrategy: {
                    type: "fitCellContents",
                },
                columnDefs: [
                    {
                        field: "name",
                        headerName: "Name",
                        cellRenderer: "agGroupCellRenderer",
                        cellRendererParams: {
                            innerRenderer: (params: any) => {
                                return createNameCell(params);
                            },
                            suppressCount: true,
                            suppressDoubleClickExpand: false
                        },
                        pinned: "left",
                        lockPinned: true
                    },
                    {
                        field: "email",
                        headerName: "Email"
                    },
                    {
                        field: "oid",
                        headerName: "OID"
                    },
                    {
                        field: "user_type",
                        headerName: "User Type",
                        enableRowGroup: true,
                        cellRenderer: (params: any) => {
                            return createUserTypeCell(params);
                        },
                        filter: "agSetColumnFilter",
                        filterParams: {
                            excelMode: "mac",
                        },
                    },
                    {
                        field: "user_role",
                        headerName: "Role",
                        enableRowGroup: true,
                        valueFormatter: (params: any) => {
                            return params.value ? params.value.charAt(0).toUpperCase() + params.value.slice(1) : '';
                        }
                    }
                ],
                groupDisplayType: "groupRows",
                masterDetail: true,
                detailRowHeight: 56,
                suppressExpandablePivotGroups: false,
                rowGroupPanelShow: 'always',
                isRowMaster: () => true,
                onGridReady: (params) => {
                    gridApi = params.api;
                    if (users.length > 0) {
                        setTimeout(() => {
                            updateGridData();
                        }, 0);
                    }
                },
                detailCellRenderer: (params: any) => {
                    return createDetailCell(params.data);
                }
            };

            const gridElement = document.querySelector("#userGrid") as HTMLElement;
            gridApi = createGrid(gridElement, gridOptions);

            gridElement.addEventListener("click", (event: MouseEvent) => {
                const target = event.target as HTMLElement;
                if (target.tagName === "A") {
                    event.preventDefault();
                    const user_id = target.getAttribute("data-user-id");
                    if (user_id) {
                        onOpenClick(user_id);
                    }
                }
            });
        })();

    });

    function onOpenClick(user_id: string) {
        navigateTo("user", user_id);
    }

    async function onDeleteClick(user_id: string) {
        objectToDelete = users.find((u) => u.user_id === user_id);
        if (objectToDelete) {
            deleteDialogOpen = true;
        }
    }

    $: if (objectToDelete) {
    }

    function onEditClick(user_id: string) {
        editObject("user", user_id);
    }

    function createNameCell(params: any) {
        const name = params.data.name;
        const span = document.createElement("span");
        span.classList.add("grid-cell");
        span.innerHTML = name;

        const buttonConfigs = [
            {
                type: "edit",
                icon: "edit",
                onClick: onEditClick,
                isVisible: (data: any) => {
                    return canManageUsers;
                },
            },
            {
                type: "delete",
                icon: "delete",
                onClick: onDeleteClick,
                isVisible: () => {
                    return (
                        userRole() === "admin" || canManageUsers
                    );
                },
            },
        ];

        const visibleButtons = buttonConfigs.filter((btn) =>
            btn.isVisible(params.data)
        );

        if (visibleButtons.length > 0) {
            const buttonGroup = document.createElement("span");
            buttonGroup.classList.add("grid-button-group");
            addActionButtons(buttonGroup, params, visibleButtons);
            span.appendChild(buttonGroup);
        }
        return span;
    }

    function createUserTypeCell(params: any) {
        const userType = formatUserType(
        params.data.user_type
        );
        const span = document.createElement("span");
        const span1 = document.createElement("span");
        span.classList.add("grid-cell", "user-type");
        span1.classList.add("user-type", userType.toLowerCase().replace(' ', '-'));
        span1.innerHTML = userType;
        span.appendChild(span1);
        return span;
    }

    function createDetailCell(data: any) {
        const div = document.createElement('div');
        div.classList.add('grid-detail');

        const labelSpan = document.createElement('span');
        labelSpan.classList.add('label');

        const valueSpan = document.createElement('span');
        valueSpan.classList.add('value');

        if (data.user_type === "oh-user" && data.user_role === "admin") {
            labelSpan.textContent = "Customers:";
            valueSpan.textContent = "ALL";
            div.appendChild(labelSpan);
            div.appendChild(valueSpan);
            return div;
        }

        const customers = (Array.isArray(data.customers) ? data.customers : [])
            .filter((c: any): c is Customer => c && typeof c === 'object' && c.name)
            .sort((a: Customer, b: Customer) => a.name.localeCompare(b.name));

        if (customers.length === 0) {
            labelSpan.textContent = "Customer:";
            valueSpan.textContent = "None";
        } else if (customers.length === 1) {
            labelSpan.textContent = "Customer:";
            valueSpan.textContent = customers[0].name;
        } else {
            labelSpan.textContent = "Customers:";
            valueSpan.textContent = customers.map((c: Customer) => c.name).join(', ');
        }

        div.appendChild(labelSpan);
        div.appendChild(valueSpan);
        return div;
    }

    function addActionButtons(span: any, params: any, buttonConfigs: any) {
        buttonConfigs.forEach((config: any) => {
            const button = document.createElement("button");
            button.classList.add("grid-button", `${config.type}-button`);
            button.innerHTML = getSVGIcon(config.icon);
            if (config.text) {
                button.innerHTML += config.text;
                button.classList.add("text");
            }
            button.addEventListener("click", () =>
                config.onClick(params.data.user_id)
            );
            span.appendChild(button);
        });
        return span;
    }

</script>

<GridHeader
        title="Users"
        objectType="user"
        on:refresh={() => loadUsers()}
        canAdd={canManageUsers}
        on:add={() => {
            $drawerStore = {
                id: "user-grid",
                instanceId: "user-grid",
                open: true,
                action: "add",
                objectType: "user",
                object: {
                    user: {
                        user_id: "",
                        name: "",
                        email: "",
                        oid: "",
                        user_type: "",
                        user_role: "",
                        claims: {},
                        metadata: {},
                        customers: []
                    },
                    lookups: {
                        customer: { list: [] },
                        user_type: { list: [] }
                    }
                }
            };
        }}
/>
<div id="userGrid" class="ag-theme-quartz ag-grid" data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"></div>
<DeleteObjectDialog
    bind:open={deleteDialogOpen}
    objectType="user"
    object={objectToDelete}
    on:delete={async (event) => {
        if (event.detail?.result) {
            users = users.filter(
                (u) =>
                    u.user_id !== objectToDelete.user_id && u !== objectToDelete
            );
            updateGridData();

            $drawerStore = {
                id: "user-grid",
                instanceId: "user-grid",
                open: false,
                action: null,
                objectType: null,
                object: null,
            };

            deleteDialogOpen = false;
            objectToDelete = null;
        }
    }}
    on:cancel={() => {
        deleteDialogOpen = false;
        objectToDelete = null;
    }}
/>

<style>
    .ag-grid {
        height: 100%;
        width: fit-content !important;
        min-width: 800px;
    }

    :global(.grid-detail) {
        padding: 1rem 3rem;
        font-size: 0.75rem;
    }

    :global(.grid-detail .label) {
        font-weight: 500;
        margin-right: 0.5rem;
    }

    :global(.grid-detail .value) {
        color: var(--text-color-secondary);
    }
</style>